import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Link,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  InputGroup,
  InputRightElement,
  IconButton,
  FormErrorMessage,
  Box,
  useToast,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import AuthLayout from '../components/auth/AuthLayout';
import { useAuthStore } from '../store/authStore';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, loading } = useAuthStore();
  const toast = useToast(); // Initialize toast
  
  // Check localStorage for confirmation failures
  const hadConfirmationError = localStorage.getItem('authConfirmationFailed') === 'true';
  const confirmationEmail = localStorage.getItem('authConfirmationEmail');
  
  const [email, setEmail] = useState(confirmationEmail || location.state?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [showVerificationMessage] = useState(
    Boolean(location.state?.fromRegistration) || hadConfirmationError
  );
  
  // Clear confirmation error flags once used
  useEffect(() => {
    if (hadConfirmationError) {
      localStorage.removeItem('authConfirmationFailed');
      localStorage.removeItem('authConfirmationEmail');
    }
  }, [hadConfirmationError]);

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate email
    if (!validateEmail(email)) {
      setIsEmailInvalid(true);
      return;
    }

    // Validate password
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      await signIn(email, password);
      // Let the AuthRoute component handle the redirection based on profile existence
      // instead of hardcoding the navigation to dashboard
      navigate('/');
    } catch (err: any) {
      console.error('Login error:', err);
      
      // Provide more helpful error messages
      if (err.message.includes('Invalid login credentials')) {
        setError('Invalid email or password. If you\'re a new user, please register first.');
        toast({
          title: 'Login Failed',
          description: 'Invalid email or password. Please check your credentials and try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else if (err.message.includes('Email not confirmed')) {
        setError('Please confirm your email address before signing in. Check your inbox for a confirmation email.');
        toast({
          title: 'Email Not Verified',
          description: 'Please confirm your email address before signing in.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else {
        setError(err.message || 'Failed to sign in. Please try again.');
        toast({
          title: 'Login Error',
          description: err.message || 'Failed to sign in. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    }
  };

  return (
    <AuthLayout 
      title="Sign in to your account" 
      subtitle="Track your billable hours with ease"
    >
      <form onSubmit={handleSubmit}>
        <Stack spacing={4}>
          {showVerificationMessage && (
            <Alert status="info" borderRadius="md" mb={4}>
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Verification Email Sent</AlertTitle>
                <AlertDescription fontSize="sm">
                  Please check your email and click the verification link before signing in.
                  Once your email is verified, you can log in below.
                </AlertDescription>
              </Box>
            </Alert>
          )}
        
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Login Failed</AlertTitle>
                <AlertDescription fontSize="sm">{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          <FormControl id="email" isRequired isInvalid={isEmailInvalid}>
            <FormLabel>Email address</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setIsEmailInvalid(false);
              }}
            />
            {isEmailInvalid && (
              <FormErrorMessage>Please enter a valid email address</FormErrorMessage>
            )}
          </FormControl>

          <FormControl id="password" isRequired>
            <FormLabel>Password</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <InputRightElement width="4.5rem">
                <IconButton
                  h="1.75rem"
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  variant="ghost"
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <Stack spacing={6}>
            <Stack
              direction={{ base: 'column', sm: 'row' }}
              align="start"
              justify="space-between"
            >
              <Link as={RouterLink} to="/reset-password" color="blue.500">
                Forgot password?
              </Link>
            </Stack>

            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              loadingText="Signing in..."
            >
              Sign in
            </Button>
          </Stack>

          <Stack pt={6}>
            <Link as={RouterLink} to="/register" color="blue.500" textAlign="center">
              Don't have an account? Sign up
            </Link>
          </Stack>
        </Stack>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;